// Types for the Import Case Wizard

export interface ExtractedAddress {
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface ExtractedCourtAddress {
  county?: string;
  branchName?: string;
  streetAddress?: string;
  city?: string;
  zipCode?: string;
}

export interface ExtractedEmergencyContact {
  name?: string;
  relationship?: string;
  phoneNumber?: string;
  alternatePhone?: string;
  address?: ExtractedAddress;
}

export interface ExtractedParent {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  alternatePhone?: string;
  relationshipToChild?: string;
  custodyStatus?: string;
  address?: ExtractedAddress;
  dateOfBirth?: string;
  notes?: string;

  // Demographic fields
  sex?: string;
  race?: string;
  primaryLanguage?: string;
  translatorNeeded?: boolean;
  maritalStatus?: string;
  incomeLevel?: string;
  educationLevel?: string;
  employmentStatus?: string;
  housingStatus?: string;
  isRuralArea?: boolean;

  // Health information
  medicalProblems?: string;
  allergies?: string;

  // Emergency contact
  emergencyContact?: ExtractedEmergencyContact;

  // Attorney information
  attorneyName?: string;
  attorneyPhone?: string;

  // Role flags
  isVisitingParent?: boolean;
  isCustodialParent?: boolean;

  // Victimization information
  primaryVictimizationType?: string;
  primaryVictimizationOther?: string;
  offenderName?: string;
}

export interface ExtractedChild {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  age?: string;
  gender?: string;
  hasSpecialNeeds?: boolean;
  specialNeeds?: string;
  allergies?: string;
  medicalConditions?: string;
  notes?: string;

  // Custody/Guardian information
  residesWithName?: string;
  legalGuardianName?: string;
  lastContactWithVisitingParent?: string;

  // DSS/Social Services information
  isInDSSCustody?: boolean;
  socialWorkerName?: string;
  socialWorkerPhone?: string;

  // Victimization information
  victimizationType?: string;
  hasWitnessedAbuse?: boolean;
  hasIntervenedInAbuse?: boolean;
  interventionDetails?: string;
}

export interface ExtractedCase {
  identifier?: string;
  courtAddress?: ExtractedCourtAddress;
  reasonForSupervision?: string;
  referralSource?: string;
  notes?: string;

  // Service type
  serviceType?: string;

  // Intake information
  intakeDate?: string;
  scheduledOrientationDate?: string;

  // Court/Legal information
  isCourtOrdered?: boolean;
  hasDV50B?: boolean;
  has50BProtectionOrder?: boolean;
  protectionOrder50BDate?: string;
  has50COrder?: boolean;
  order50CDate?: string;
  exParteStatus?: string;
  judgeName?: string;

  // Safety information
  safetyInformation?: string;

  // Counseling information
  inCounseling?: boolean;
  courtOrderedCounseling?: boolean;
  interestedInTherapeuticVisits?: boolean;
}

export interface ExtractedIntakeData {
  case: ExtractedCase;
  parents: ExtractedParent[];
  children: ExtractedChild[];
  confidence?: number;
  extractionNotes?: string;
}

// Form data types (with required fields for form validation)
export interface ParentFormData {
  id: string; // Unique ID for React key
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  alternatePhone: string;
  relationshipToChild: string;
  custodyStatus: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  feePercentage: number;

  // Demographic fields
  dateOfBirth: string;
  sex: string;
  race: string;
  primaryLanguage: string;
  translatorNeeded: boolean;
  maritalStatus: string;
  incomeLevel: string;
  educationLevel: string;
  employmentStatus: string;
  housingStatus: string;
  isRuralArea: boolean;

  // Health information
  medicalProblems: string;
  allergies: string;

  // Emergency contact
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;

  // Attorney information
  attorneyName: string;

  // Role flags
  isVisitingParent: boolean;
  isCustodialParent: boolean;
}

export interface ChildFormData {
  id: string; // Unique ID for React key
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  hasSpecialNeeds: boolean;
  specialNeeds: string;
  allergies: string;
  medicalConditions: string;
  notes: string;

  // Custody/Guardian information
  residesWithName: string;
  legalGuardianName: string;

  // DSS/Social Services information
  isInDSSCustody: boolean;
  socialWorkerName: string;
  socialWorkerPhone: string;

  // Victimization information
  victimizationType: string;
  hasWitnessedAbuse: boolean;
}

export interface CaseFormData {
  identifier: string;
  courtCounty: string;
  courtBranchName: string;
  courtStreetAddress: string;
  courtCity: string;
  courtZipCode: string;
  reasonForSupervision: string;
  referralSource: string;
  notes: string;

  // Service types (multi-select)
  serviceTypes: string[];

  // Court/Legal information
  isCourtOrdered: boolean;
  hasDV50B: boolean;
  exParteStatus: string;
  judgeName: string;

  // Safety information
  safetyInformation: string;
}

export interface WizardFormData {
  case: CaseFormData;
  parents: ParentFormData[];
  children: ChildFormData[];
}

// Step props
export interface StepProps {
  formData: WizardFormData;
  setFormData: React.Dispatch<React.SetStateAction<WizardFormData>>;
  onNext: () => void;
  onBack: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

// Uploaded file info
export interface UploadedFileInfo {
  storagePath: string;
  fileName: string;
  downloadUrl: string;
}

// Review step specific props
export interface ReviewStepProps {
  formData: WizardFormData;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  onEditStep: (step: number) => void;
  isSubmitting: boolean;
}

// Constants
export const RELATIONSHIP_OPTIONS = [
  { value: "mother", label: "Mother" },
  { value: "father", label: "Father" },
  { value: "step-mother", label: "Step-Mother" },
  { value: "step-father", label: "Step-Father" },
  { value: "grandmother", label: "Grandmother" },
  { value: "grandfather", label: "Grandfather" },
  { value: "aunt", label: "Aunt" },
  { value: "uncle", label: "Uncle" },
  { value: "sister", label: "Sister" },
  { value: "brother", label: "Brother" },
  { value: "other", label: "Other" },
];

export const CUSTODY_STATUS_OPTIONS = [
  { value: "custodial", label: "Custodial" },
  { value: "non-custodial", label: "Non-Custodial" },
  { value: "joint-custodial", label: "Joint Custodial" },
];

export const REASON_FOR_SUPERVISION_OPTIONS = [
  { value: "domestic_violence", label: "Domestic Violence" },
  { value: "child_abuse", label: "Child Abuse" },
  { value: "child_neglect", label: "Child Neglect" },
  { value: "substance_abuse", label: "Substance Abuse" },
  { value: "mental_health", label: "Mental Health" },
  { value: "parental_conflict", label: "Parental Conflict" },
  { value: "reunification", label: "Reunification" },
  { value: "court_ordered", label: "Court Ordered" },
  { value: "safety_concerns", label: "Safety Concerns" },
  { value: "other", label: "Other" },
];

export const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

export const SERVICE_TYPE_OPTIONS = [
  { value: "supervised_visitation", label: "Supervised Visitation" },
  { value: "safe_exchange", label: "Safe Exchange" },
  { value: "case_management", label: "Case Management: Strengthening Families (Parenting)" },
];

export const EX_PARTE_STATUS_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "denied", label: "Denied" },
  { value: "not_requested", label: "Not Requested" },
  { value: "na", label: "N/A" },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
  { value: "separated", label: "Separated" },
  { value: "widowed", label: "Widower" },
  { value: "partner", label: "Partner" },
];

export const INCOME_LEVEL_OPTIONS = [
  { value: "under_15000", label: "Under $15,000" },
  { value: "15000_29999", label: "$15,000 - $29,999" },
  { value: "30000_49999", label: "$30,000 - $49,999" },
  { value: "50000_74999", label: "$50,000 - $74,999" },
  { value: "75000_99999", label: "$75,000 - $99,999" },
  { value: "over_100000", label: "Over $100,000" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
];

export const EMPLOYMENT_STATUS_OPTIONS = [
  { value: "employed_full_time", label: "Employed Full-Time" },
  { value: "employed_part_time", label: "Employed Part-Time" },
  { value: "unemployed", label: "Unemployed" },
  { value: "self_employed", label: "Self-Employed" },
  { value: "student", label: "Student" },
  { value: "retired", label: "Retired" },
  { value: "disabled", label: "Disabled" },
  { value: "homemaker", label: "Homemaker" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
];

export const HOUSING_STATUS_OPTIONS = [
  { value: "stable", label: "Stable" },
  { value: "temporary", label: "Temporary" },
  { value: "homeless", label: "Homeless" },
  { value: "transitional", label: "Transitional" },
  { value: "unknown", label: "Unknown" },
];

export const EDUCATION_LEVEL_OPTIONS = [
  { value: "hs_ged", label: "High School / GED" },
  { value: "some_college", label: "Some College" },
  { value: "two_year", label: "2-Year Degree" },
  { value: "four_year", label: "4-Year Degree" },
  { value: "masters", label: "Master's Degree" },
  { value: "phd", label: "PhD / Doctorate" },
];

export const LANGUAGE_OPTIONS = [
  { value: "english", label: "English" },
  { value: "spanish", label: "Spanish" },
  { value: "chinese", label: "Chinese" },
  { value: "vietnamese", label: "Vietnamese" },
  { value: "tagalog", label: "Tagalog" },
  { value: "korean", label: "Korean" },
  { value: "arabic", label: "Arabic" },
  { value: "russian", label: "Russian" },
  { value: "french", label: "French" },
  { value: "portuguese", label: "Portuguese" },
  { value: "other", label: "Other" },
];

export const VICTIMIZATION_TYPE_OPTIONS = [
  { value: "domestic_violence", label: "Domestic Violence" },
  { value: "sexual_assault", label: "Sexual Assault" },
  { value: "emotional", label: "Emotional" },
  { value: "stalking", label: "Stalking" },
  { value: "other", label: "Other" },
  { value: "none", label: "None" },
  { value: "na", label: "N/A" },
];

// Helper to generate unique IDs
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 11);
};

// Helper to create empty form data
export const createEmptyParent = (): ParentFormData => ({
  id: generateId(),
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  alternatePhone: "",
  relationshipToChild: "",
  custodyStatus: "",
  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
  feePercentage: 50,

  // Demographic fields
  dateOfBirth: "",
  sex: "",
  race: "",
  primaryLanguage: "",
  translatorNeeded: false,
  maritalStatus: "",
  incomeLevel: "",
  educationLevel: "",
  employmentStatus: "",
  housingStatus: "",
  isRuralArea: false,

  // Health information
  medicalProblems: "",
  allergies: "",

  // Emergency contact
  emergencyContactName: "",
  emergencyContactRelationship: "",
  emergencyContactPhone: "",

  // Attorney information
  attorneyName: "",

  // Role flags
  isVisitingParent: false,
  isCustodialParent: false,
});

export const createEmptyChild = (): ChildFormData => ({
  id: generateId(),
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  gender: "",
  hasSpecialNeeds: false,
  specialNeeds: "",
  allergies: "",
  medicalConditions: "",
  notes: "",

  // Custody/Guardian information
  residesWithName: "",
  legalGuardianName: "",

  // DSS/Social Services information
  isInDSSCustody: false,
  socialWorkerName: "",
  socialWorkerPhone: "",

  // Victimization information
  victimizationType: "",
  hasWitnessedAbuse: false,
});

export const createEmptyCase = (): CaseFormData => ({
  identifier: "",
  courtCounty: "",
  courtBranchName: "",
  courtStreetAddress: "",
  courtCity: "",
  courtZipCode: "",
  reasonForSupervision: "",
  referralSource: "",
  notes: "",

  // Service types (multi-select)
  serviceTypes: [],

  // Court/Legal information
  isCourtOrdered: false,
  hasDV50B: false,
  exParteStatus: "",
  judgeName: "",

  // Safety information
  safetyInformation: "",
});

export const createInitialFormData = (): WizardFormData => ({
  case: createEmptyCase(),
  parents: [createEmptyParent(), createEmptyParent()],
  children: [createEmptyChild()],
});

// Helper to convert extracted data to form data
export const extractedToFormData = (extracted: ExtractedIntakeData): WizardFormData => {
  const caseData: CaseFormData = {
    identifier: extracted.case.identifier || "",
    courtCounty: extracted.case.courtAddress?.county || "",
    courtBranchName: extracted.case.courtAddress?.branchName || "",
    courtStreetAddress: extracted.case.courtAddress?.streetAddress || "",
    courtCity: extracted.case.courtAddress?.city || "",
    courtZipCode: extracted.case.courtAddress?.zipCode || "",
    reasonForSupervision: extracted.case.reasonForSupervision || "",
    referralSource: extracted.case.referralSource || "",
    notes: extracted.case.notes || "",

    // Service types (convert legacy single value to array)
    serviceTypes: extracted.case.serviceType
      ? extracted.case.serviceType === "both"
        ? ["supervised_visitation", "safe_exchange"]
        : extracted.case.serviceType === "supervised_visit"
          ? ["supervised_visitation"]
          : [extracted.case.serviceType]
      : [],

    // Court/Legal information
    isCourtOrdered: extracted.case.isCourtOrdered || false,
    hasDV50B: extracted.case.hasDV50B || false,
    exParteStatus: extracted.case.exParteStatus || "",
    judgeName: extracted.case.judgeName || "",

    // Safety information
    safetyInformation: extracted.case.safetyInformation || "",
  };

  const parents: ParentFormData[] = extracted.parents.length > 0
    ? extracted.parents.map((p) => ({
        id: generateId(),
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        email: p.email || "",
        phoneNumber: p.phoneNumber || "",
        alternatePhone: p.alternatePhone || "",
        relationshipToChild: p.relationshipToChild || "",
        custodyStatus: p.custodyStatus || "",
        streetAddress: p.address?.streetAddress || "",
        city: p.address?.city || "",
        state: p.address?.state || "",
        zipCode: p.address?.zipCode || "",
        feePercentage: 50,

        // Demographic fields
        dateOfBirth: p.dateOfBirth || "",
        sex: p.sex || "",
        race: p.race || "",
        primaryLanguage: p.primaryLanguage || "",
        translatorNeeded: p.translatorNeeded || false,
        maritalStatus: p.maritalStatus || "",
        incomeLevel: p.incomeLevel || "",
        educationLevel: p.educationLevel || "",
        employmentStatus: p.employmentStatus || "",
        housingStatus: p.housingStatus || "",
        isRuralArea: p.isRuralArea || false,

        // Health information
        medicalProblems: p.medicalProblems || "",
        allergies: p.allergies || "",

        // Emergency contact
        emergencyContactName: p.emergencyContact?.name || "",
        emergencyContactRelationship: p.emergencyContact?.relationship || "",
        emergencyContactPhone: p.emergencyContact?.phoneNumber || "",

        // Attorney information
        attorneyName: p.attorneyName || "",

        // Role flags
        isVisitingParent: p.isVisitingParent || false,
        isCustodialParent: p.isCustodialParent || false,
      }))
    : [createEmptyParent(), createEmptyParent()];

  const children: ChildFormData[] = extracted.children.length > 0
    ? extracted.children.map((c) => ({
        id: generateId(),
        firstName: c.firstName || "",
        lastName: c.lastName || "",
        dateOfBirth: c.dateOfBirth || "",
        gender: c.gender || "",
        hasSpecialNeeds: c.hasSpecialNeeds || false,
        specialNeeds: c.specialNeeds || "",
        allergies: c.allergies || "",
        medicalConditions: c.medicalConditions || "",
        notes: c.notes || "",

        // Custody/Guardian information
        residesWithName: c.residesWithName || "",
        legalGuardianName: c.legalGuardianName || "",

        // DSS/Social Services information
        isInDSSCustody: c.isInDSSCustody || false,
        socialWorkerName: c.socialWorkerName || "",
        socialWorkerPhone: c.socialWorkerPhone || "",

        // Victimization information
        victimizationType: c.victimizationType || "",
        hasWitnessedAbuse: c.hasWitnessedAbuse || false,
      }))
    : [createEmptyChild()];

  return { case: caseData, parents, children };
};
